export default function ComparisonChecklist() {
  const questions = [
    {
      question: 'Are you licensed and insured for commercial construction in Texas?',
      why: 'Commercial construction requires comprehensive licensing and insurance including general liability, workers\' compensation, and often bonding. Verify their Texas contractor license and insurance certificates. Commercial projects involve higher risks and values than residential work, making proper coverage essential for your protection.',
      redFlag: 'Contractors who can\'t provide current commercial insurance certificates or proper licensing should be rejected immediately.'
    },
    {
      question: 'How many commercial projects have you completed in El Paso?',
      why: 'Local commercial experience demonstrates understanding of El Paso building codes, permitting processes, and reliable subcontractors. Ask to see a portfolio of completed projects similar to yours. Visit recent projects to assess quality, attention to detail, and how well they function for tenants or owners.',
      redFlag: 'Be cautious of contractors without local commercial experience who may not understand regional requirements and challenges.'
    },
    {
      question: 'Can you provide references from recent commercial clients?',
      why: 'Recent commercial references demonstrate current capabilities and client satisfaction. Contact at least 3 references to ask about budget management, timeline adherence, communication, and problem-solving. Ask if they would hire the contractor again and if the project met their business needs.',
      redFlag: 'Contractors who can\'t provide recent commercial references or seem hesitant may have dissatisfied clients or limited experience.'
    },
    {
      question: 'What is your project management approach?',
      why: 'Commercial projects require sophisticated project management including scheduling, budget tracking, subcontractor coordination, and quality control. Ask about their project management software, communication protocols, and how they handle multiple concurrent projects. Strong project management prevents delays and cost overruns.',
      redFlag: 'Vague descriptions of project management or lack of formal systems indicate potential problems with complex commercial projects.'
    },
    {
      question: 'Who will be the dedicated project manager for my project?',
      why: 'Commercial projects need experienced project managers who serve as your primary contact. Ask about their experience, qualifications, and how often they\'ll be on-site. Quality contractors assign dedicated managers who coordinate all aspects of construction and maintain consistent communication throughout the 6-24 month timeline.',
      redFlag: 'Contractors who can\'t identify a dedicated project manager or seem to spread managers too thin raise concerns about oversight.'
    },
    {
      question: 'What is your typical timeline and how do you ensure on-time completion?',
      why: 'Commercial construction timelines directly impact your business operations and revenue. Contractors should provide detailed schedules with critical path analysis and explain their strategies for staying on schedule. Ask about their track record for on-time completion and how they handle delays beyond their control.',
      redFlag: 'Unrealistic timelines or inability to discuss schedule management strategies indicate poor planning or inexperience.'
    },
    {
      question: 'How do you manage budgets and handle change orders?',
      why: 'Commercial budgets are complex with many variables. Quality contractors provide detailed cost breakdowns, regular budget updates, and formal change order processes. They should explain how they handle unforeseen conditions, material price fluctuations, and scope changes. Transparent financial management prevents surprise costs.',
      redFlag: 'Vague budgets, resistance to detailed cost breakdowns, or informal change order processes are major red flags.'
    },
    {
      question: 'What safety protocols do you follow?',
      why: 'Commercial construction sites have significant safety risks. Contractors must follow OSHA regulations, maintain safety programs, and ensure all workers are properly trained. Ask about their safety record, incident rates, and how they enforce safety compliance. Your liability exposure depends on their safety practices.',
      redFlag: 'Contractors who dismiss safety questions or lack formal safety programs put your project and people at risk.'
    },
    {
      question: 'How do you ensure quality control throughout construction?',
      why: 'Quality control systems ensure work meets specifications and standards. Ask about their inspection processes, quality checkpoints, and how they handle deficiencies. Quality contractors conduct regular inspections, document work progress, and address issues immediately rather than at project end.',
      redFlag: 'Lack of formal quality control processes or reliance solely on final inspections often results in costly rework.'
    },
    {
      question: 'What warranties do you provide on commercial construction?',
      why: 'Commercial construction warranties typically cover workmanship for 1-2 years and should address major systems. Get all warranty terms in writing including coverage, exclusions, and claim procedures. Quality contractors stand behind their work and respond promptly to warranty issues that could impact your business operations.',
      redFlag: 'Minimal warranties or only verbal promises indicate lack of confidence in construction quality or financial stability.'
    }
  ];

  return (
    <section id="checklist" className="py-20 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            10 Questions to Ask Your El Paso Commercial Contractor
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Use this comprehensive checklist when interviewing commercial contractors. These critical questions help you identify qualified professionals and avoid costly mistakes. Commercial construction is a major investment—choose wisely.
          </p>
        </div>

        <div className="bg-white rounded-xl border-2 border-teal-600 p-8 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
              <i className="ri-lightbulb-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">How to Use This Checklist</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                Interview at least 3 commercial contractors before making your decision. Ask each question and take detailed notes on their responses. Pay attention to their professionalism, communication style, and depth of commercial experience. Compare responses to identify the contractor best suited for your commercial project.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((item, index) => (
            <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-600 text-white font-bold rounded-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{item.question}</h3>
                    
                    <div className="space-y-3">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-blue-900 mb-1 flex items-center gap-2">
                          <i className="ri-information-line"></i>
                          Why This Matters
                        </p>
                        <p className="text-sm text-blue-900 leading-relaxed">{item.why}</p>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4">
                        <p className="text-xs font-semibold text-red-900 mb-1 flex items-center gap-2">
                          <i className="ri-alert-line"></i>
                          Red Flag Warning
                        </p>
                        <p className="text-sm text-red-900 leading-relaxed">{item.redFlag}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            Additional Tips for Choosing a Commercial Contractor
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm text-white/90">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Get Multiple Bids:</strong> Obtain at least 3 detailed proposals to compare pricing, approach, and value. The lowest bid isn\'t always the best choice for commercial projects.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Check Financial Stability:</strong> Commercial projects require contractors with strong financial backing. Request financial references and verify they can handle your project size.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Review Contracts Carefully:</strong> Have an attorney review the construction contract. Ensure all terms, schedules, and responsibilities are clearly documented.</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Visit Active Job Sites:</strong> Tour current projects to observe work quality, site organization, safety practices, and how they manage active construction.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Verify Bonding Capacity:</strong> For large projects, ensure contractors have adequate bonding capacity to protect your investment if they fail to complete the work.</p>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-teal-400 text-xl flex-shrink-0 mt-0.5"></i>
                <p><strong className="text-white">Document Everything:</strong> Maintain detailed records of all communications, decisions, changes, and payments throughout the project.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
